import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, Star, Mail, Phone, Smartphone, Building2 } from 'lucide-react';
import { useContact, useDeleteContact } from '../hooks/useContacts.js';
import { toast } from '../lib/toast.js';
import ContactModal from '../components/contacts/ContactModal.js';

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('contacts');
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: contact, isLoading, error } = useContact(id ?? '');
  const deleteContact = useDeleteContact();

  async function handleDelete() {
    if (!contact) return;
    const name = `${contact.firstName} ${contact.lastName}`;
    if (!confirm(t('detail.delete_confirm', { name }))) return;
    try {
      await deleteContact.mutateAsync(contact.id);
      toast.success(t('toast.deleted'));
      navigate('/contacts');
    } catch (_err) {
      toast.error(t('toast.delete_error'));
    }
  }

  if (isLoading) {
    return (
      <div className="page-enter">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="skeleton-line" style={{ width: 200, height: 24 }} />
          <div className="skeleton-block" style={{ height: 120, borderRadius: 'var(--radius-lg)' }} />
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="page-enter" style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
        <Users size={48} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-4)' }} />
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
          {t('errors.not_found', { ns: 'common' })}
        </p>
      </div>
    );
  }

  const name = `${contact.firstName} ${contact.lastName}`;

  return (
    <div className="page-enter">
      {/* Back + header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <button
          onClick={() => navigate(-1)}
          aria-label={t('actions.back', { ns: 'common' })}
          style={{
            background: 'none', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', cursor: 'pointer',
            color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center',
            gap: 'var(--space-1)', padding: 'var(--space-1) var(--space-3)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <ArrowLeft size={14} /> {t('actions.back', { ns: 'common' })}
        </button>
        <h1 className="page-title" style={{ flex: 1 }}>{name}</h1>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={() => setEditOpen(true)}
            style={{
              padding: 'var(--space-2) var(--space-4)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              color: 'var(--color-text)', background: 'var(--color-bg)', cursor: 'pointer',
            }}
          >
            {t('detail.edit_button')}
          </button>
          <button
            onClick={handleDelete}
            style={{
              padding: 'var(--space-2) var(--space-4)', border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              color: 'var(--color-danger)', background: 'var(--color-bg)', cursor: 'pointer',
            }}
          >
            {t('detail.delete_button')}
          </button>
        </div>
      </div>

      {/* Summary card */}
      <div style={{
        background: 'var(--color-bg)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)',
        marginBottom: 'var(--space-6)',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 'var(--space-5)',
      }}>
        {contact.title && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('form.title_label')}</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{contact.title}</p>
          </div>
        )}
        {contact.email && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Mail size={12} /> {t('form.email_label')}
            </p>
            <a href={`mailto:${contact.email}`}
              style={{ fontSize: 'var(--text-sm)', color: 'var(--color-brand)', textDecoration: 'none' }}>
              {contact.email}
            </a>
          </div>
        )}
        {contact.phone && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Phone size={12} /> {t('form.phone_label')}
            </p>
            <a href={`tel:${contact.phone}`} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
              {contact.phone}
            </a>
          </div>
        )}
        {contact.mobile && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Smartphone size={12} /> {t('form.mobile_label')}
            </p>
            <a href={`tel:${contact.mobile}`} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
              {contact.mobile}
            </a>
          </div>
        )}
      </div>

      {/* Companies */}
      <div style={{
        background: 'var(--color-bg)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
        marginBottom: 'var(--space-6)',
      }}>
        <p className="label" style={{ marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Building2 size={14} /> {t('detail.companies_tab')}
        </p>
        {!contact.companies || contact.companies.length === 0 ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {t('detail.no_companies')}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {contact.companies.map((link) => (
              <div key={link.companyId} style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                padding: 'var(--space-3)', background: 'var(--color-bg-subtle)',
                borderRadius: 'var(--radius-md)',
              }}>
                {link.isPrimary && (
                  <Star size={14} style={{ color: 'var(--color-brand)', flexShrink: 0 }} fill="currentColor" />
                )}
                <div style={{ flex: 1 }}>
                  <Link
                    to={`/companies/${link.companyId}`}
                    style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-brand)', textDecoration: 'none' }}
                  >
                    {link.companyName}
                  </Link>
                  {link.role && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{link.role}</p>
                  )}
                </div>
                {link.isPrimary && (
                  <span style={{
                    fontSize: 'var(--text-xs)', color: 'var(--color-brand)',
                    fontWeight: 'var(--font-medium)', background: 'var(--color-brand-pale)',
                    padding: '2px var(--space-2)', borderRadius: 'var(--radius-full)',
                  }}>
                    {t('detail.primary_badge')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      {contact.notes && (
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
        }}>
          <p className="label" style={{ marginBottom: 'var(--space-3)' }}>
            {t('form.notes_label')}
          </p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', lineHeight: 'var(--leading-relaxed)', whiteSpace: 'pre-wrap' }}>
            {contact.notes}
          </p>
        </div>
      )}

      {editOpen && (
        <ContactModal contact={contact} onClose={() => setEditOpen(false)} />
      )}
    </div>
  );
}
